import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Field,
  Form,
  FormSizes,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  useFormState,
} from '@carboledger/ui';
import { isFormDirty } from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { COMPANY_PROFILE_FIELDS } from '../../constants/accountSetup.constants';
import { companyProfileValidation } from '../../accountSetupValidation';
import {
  hasRequiredCompanyProfile,
  isCompanyProfileFieldRequired,
} from '../../utils/companyProfile';
import { CompanyIdField } from './CompanyIdField';
import styles from './CompanyProfileForm.module.scss';

const DirtyReporter = ({ initialValues, onDirtyChange }) => {
  const { values } = useFormState();
  const isDirty = isFormDirty(values, initialValues);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  return null;
};

const CompanyProfileField = ({ name, label, placeholder, rules, disabled = false }) => (
  <Field name={name} required={isCompanyProfileFieldRequired(name)} rules={rules}>
    {({ id, name: inputName, value, onChange, onBlur, status, message, required }) => (
      <TextInput
        id={id}
        name={inputName}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        status={status}
        message={message}
        required={required}
        disabled={disabled}
      />
    )}
  </Field>
);

/**
 * Props:
 *   initialValues  the saved profile as form values
 *   onSubmit       (values) => void
 *   isSubmitting   disables Save while the save is in flight
 *   onDirtyChange  (isDirty) => void — fires whenever the edited/saved comparison flips
 */
export const CompanyProfileForm = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  onDirtyChange,
}) => {
  const t = useTranslations();

  const [confirmingReset, setConfirmingReset] = useState(false);
  const askBeforeReset = useCallback(() => setConfirmingReset(true), []);
  const keepEditing = useCallback(() => setConfirmingReset(false), []);

  return (
    <Form
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values, setValue, reset }) => {
        const isDirty = isFormDirty(values, initialValues);
        const requestReset = isDirty ? askBeforeReset : reset;
        const discardEdits = () => {
          setConfirmingReset(false);
          reset();
        };

        return (
          <>
            {onDirtyChange && (
              <DirtyReporter initialValues={initialValues} onDirtyChange={onDirtyChange} />
            )}

            <Form.Section className={styles.fields}>
              <CompanyProfileField
                name={COMPANY_PROFILE_FIELDS.companyName}
                label={t('FORM_COMPANY_NAME')}
                placeholder={t('FORM_COMPANY_NAME_PLACEHOLDER')}
              />

              <CompanyIdField
                rules={companyProfileValidation.companyId}
                onGenerate={(companyId) => setValue(COMPANY_PROFILE_FIELDS.companyId, companyId)}
              />

              <CompanyProfileField
                name={COMPANY_PROFILE_FIELDS.addressLine1}
                label={t('FORM_ADDRESS_LINE_1')}
                placeholder={t('FORM_ADDRESS_LINE_1_PLACEHOLDER')}
              />

              <CompanyProfileField
                name={COMPANY_PROFILE_FIELDS.addressLine2}
                label={t('FORM_ADDRESS_LINE_2')}
                placeholder={t('FORM_ADDRESS_LINE_2_PLACEHOLDER')}
              />

              <Form.Row columns={2}>
                <CompanyProfileField
                  name={COMPANY_PROFILE_FIELDS.city}
                  label={t('FORM_CITY_NAME')}
                  placeholder={t('FORM_CITY_NAME_PLACEHOLDER')}
                />
                <CompanyProfileField
                  name={COMPANY_PROFILE_FIELDS.country}
                  label={t('FORM_COUNTRY_NAME')}
                  placeholder={t('FORM_COUNTRY_NAME_PLACEHOLDER')}
                />
              </Form.Row>

              <Form.Row columns={2}>
                <CompanyProfileField
                  name={COMPANY_PROFILE_FIELDS.zipCode}
                  label={t('FORM_ZIP_CODE')}
                  placeholder={t('FORM_ZIP_CODE_PLACEHOLDER')}
                />
              </Form.Row>
            </Form.Section>

            <Form.Footer>
              {isDirty && (
                <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
                  {t('COMMON_DISCARD_BODY_TITLE')}
                </Typography>
              )}
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestReset}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting || !hasRequiredCompanyProfile(values)}
              >
                {t('COMMON_SAVE')}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingReset}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_QUESTION')}
              confirmLabel={t('COMMON_DISCARD_CHANGES')}
              tone="danger"
              onConfirm={discardEdits}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};
